import { IMAGE_MAX_DIMENSION_FUNDO } from '@plane/shared';
import { useState } from 'react';

import { prepararImagem } from '@/modules/projects/preparar-imagem';
import { uploadMedia, urlDaMedia } from '@/modules/site-content/services/media.service';

/**
 * Escolhe e envia uma imagem, devolvendo o identificador dela.
 *
 * O envio acontece ao escolher o arquivo, e não no "Salvar": assim quem está
 * editando vê a imagem antes de decidir. O preço é que uma imagem enviada e
 * depois descartada fica no banco sem ninguém apontando para ela — ver a nota
 * sobre isso em `hero-page.tsx`.
 */
export function CampoDeImagem({
  mediaId,
  aoTrocar,
  maiorLado = IMAGE_MAX_DIMENSION_FUNDO,
}: {
  mediaId: string | null;
  aoTrocar: (id: string | null) => void;
  maiorLado?: number;
}) {
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function escolher(arquivo: File | undefined) {
    if (!arquivo) return;

    setErro(null);
    setEnviando(true);

    try {
      const preparada = await prepararImagem(arquivo, maiorLado);
      const media = await uploadMedia(preparada);

      aoTrocar(media.id);
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível enviar a imagem.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      {mediaId !== null && (
        <div className="flex flex-col gap-2">
          <img
            src={urlDaMedia(mediaId)}
            alt=""
            className="max-w-[420px] border border-clay object-cover"
          />
          <button
            type="button"
            onClick={() => aoTrocar(null)}
            className="self-start text-xs text-brass underline"
          >
            Remover imagem
          </button>
        </div>
      )}

      <label className="flex flex-col gap-1.5">
        <span className="text-xs tracking-[0.12em] text-ink-soft uppercase">
          {mediaId === null ? 'Escolher imagem' : 'Trocar imagem'}
        </span>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          disabled={enviando}
          onChange={(evento) => {
            void escolher(evento.target.files?.[0]);
            // Limpa para que escolher o mesmo arquivo de novo dispare o evento.
            evento.target.value = '';
          }}
          className="text-sm file:mr-3 file:border file:border-clay file:bg-bone-soft file:px-3 file:py-1.5 file:text-sm"
        />
      </label>

      {enviando && <p className="text-xs text-ink-soft">Enviando…</p>}

      {erro && (
        <p role="alert" className="text-xs text-brass">
          {erro}
        </p>
      )}

      <p className="text-xs text-ink-soft">
        A imagem é reduzida e convertida no navegador antes de subir — não precisa preparar nada.
      </p>
    </div>
  );
}
